import { ArrowDownTrayIcon, ArrowsRightLeftIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import {
  byLedgerDateAsc,
  confirmSufficientFunds,
  LEDGER_DEFAULT_SORT_KEY,
  LEDGER_DEFAULT_SORT_ORDER,
} from 'components/TrustLedgerGrid'
import { baseUrl, InputType } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  createTrustLedgerInterest,
  deleteTrustLedgerInterest,
  getTrustLedgerInterest,
  transferTrustLedgerInterest,
  updateTrustLedgerInterest,
} from 'services'
import { FormTable } from 'stories/components'
import { getPrice2decimal, getPrice3decimal, removeComma, solveDecimalJavascriptSum } from 'utils'
import { setLoanNumber } from 'utils/setLoanNumber'

import { usePayoffGuard } from '../payoffGuard'
import { interestDefaultInputs, interestHeader } from './constants'
import { openingBalanceSeed } from './openingBalance'
import SubLedgerTransferModal from './SubLedgerTransferModal'
import { ISubLedgerRow } from './type'

/**
 * One of the sub-ledgers that used to live on the standalone Escrow Ledger screen.
 * All of them share the TrustLedgerInterest row store and are keyed by `type`
 * (a row with no `type` belongs to the legacy Interest Reserve ledger).
 */
export default function SubLedgerTab({ tabType, title }: { tabType: string; title: string }) {
  // Warns before entering a ledger transaction against an active payoff.
  const confirmPayoff = usePayoffGuard()
  const [loading, setLoading] = useState(false)
  const [initData, setInitData] = useState<Record<string, any>>({})
  const [interestData, setInterestData] = useState<Array<ISubLedgerRow>>([])
  const [transferring, setTransferring] = useState(false)

  const token = useSelector((state: any) => state.auth.token)
  const loan = useSelector((state: any) => state.loan ?? ({} as Record<string, any>))
  const loanNumber = setLoanNumber()

  const init = () => {
    setLoading(true)
    getTrustLedgerInterest()
      .catch(() => ({} as Record<string, any>))
      .then((interestRes) => {
        setInitData(interestRes)
        setInterestData(interestRes?.interestData ?? [])
      })
      .finally(() => setLoading(false))
  }

  const {
    interestReserve = -1,
    originalEscrowAmount,
    escrowRepairHoldback = 0,
    escrowRepairHoldbackFunded = 0,
    constructionReserve = 0,
  } = initData

  /**
   * What the holdback was committed at, as opposed to `escrowRepairHoldback`, which is the SEED for
   * the running balance below and has to stay the loan's raw configured amount.
   *
   * A converted loan carries the amount only on this ledger - the TMO conversion writes the funding
   * deposit and its draws and leaves `loan.escrowRepairHoldback` null on all but 1 of longhorn's
   * 15,231 serviced loans - so this line read "Original ... $0.00" beside a Current of $16,900.
   * The backend resolves the fallback (`subLedgerCommitment`) so the payoff, the payment screen and
   * this tab cannot state different numbers.
   */
  const escrowRepairHoldbackCommitment = removeComma(escrowRepairHoldback) || removeComma(escrowRepairHoldbackFunded)

  useEffect(() => {
    setLoading(true)
    init()
  }, [])

  const ledger = useMemo(() => {
    // Walk the bucket in date order, not the order the rows happen to sit in the jsonb array.
    // Balance is a running total, so on an imported loan - where stored order bears no relation
    // to when the transaction happened - id order produced a balance column that climbed and fell
    // at random and left the closing figure on whichever row was stored last. This is the same
    // order the backend walks for the All tab and for the construction reserve, so the one ledger
    // now reads the same whichever tab you look at it from.
    const rows = byLedgerDateAsc(
      interestData.filter((item) => (tabType === 'interest' && !item.type) || item.type === tabType),
    )

    // Suspense is funded entirely by transfers in, so it opens at zero. Draw disbursements come
    // out of the construction reserve, so that is what their ledger opens at.
    let balance = 0
    if (tabType === 'interest')
      balance = openingBalanceSeed(interestReserve === -1 ? originalEscrowAmount : interestReserve, rows)
    if (tabType === 'escrow') balance = openingBalanceSeed(originalEscrowAmount, rows)
    if (tabType === 'escrowRepairHoldback') balance = openingBalanceSeed(escrowRepairHoldback, rows)
    if (tabType === 'draw') balance = openingBalanceSeed(constructionReserve, rows)

    const opening = balance

    const walked = rows.map((item) => {
      balance = solveDecimalJavascriptSum([
        removeComma(balance),
        -1 * removeComma(item.payment),
        removeComma(item.deposit),
      ])
      item.balance = getPrice3decimal(balance)
      // Draws are mirrored from the Trust Ledger and are edited there, not on this grid.
      item.rowLocked = item.derived === true
      return item
    })

    // The opening balance travels with the rows: with nothing on the ledger yet the bucket still
    // holds whatever it was funded with, and a transaction has to be measured against that.
    return { rows: walked, opening }
  }, [interestData, interestReserve, originalEscrowAmount, escrowRepairHoldback, constructionReserve, tabType])

  const interestDataWithBalance = ledger.rows

  const currentBalance = interestDataWithBalance.length
    ? interestDataWithBalance[interestDataWithBalance.length - 1].balance
    : null

  /** What the bucket holds right now: the closing balance, or its opening one while it is empty. */
  const closingBalance = currentBalance === null ? ledger.opening : removeComma(currentBalance)

  /** What an entry takes out of the bucket, net of anything the same entry puts back in. */
  const netWithdrawal = (row: Record<string, any> | null | undefined) =>
    row ? solveDecimalJavascriptSum([removeComma(row.payment), -1 * removeComma(row.deposit)]) : 0

  const onInterestSubmit = async (currentId: any, values: Record<string, any>) => {
    // Editing a row: what it already takes out is added back, so the check measures the change.
    const edited = currentId ? interestData.find((row) => row.id === currentId) : null
    const available = solveDecimalJavascriptSum([removeComma(closingBalance), netWithdrawal(edited)])
    // Cancelling has to leave the form open with what was typed still in it, which is what
    // throwing does - FormTable swallows it. Resolving would close the row as if it had saved.
    if (!(await confirmSufficientFunds(netWithdrawal(values), available, title))) throw new Error('cancelled')

    return new Promise((resolve) => {
      setLoading(true)
      if (!currentId) {
        values = {
          id: Date.now(),
          type: tabType,
          ...values,
        }
        createTrustLedgerInterest(values)
          .then(() => {
            resolve(values)

            const temp = cloneDeep(interestData)
            temp.push(values as any)
            setInterestData(temp)
          })
          .finally(() => setLoading(false))
      } else {
        const currentItem = interestData.find((v) => v.id === currentId)
        values = {
          ...currentItem,
          ...values,
        }
        updateTrustLedgerInterest(currentId, values)
          .then(() => resolve(values))
          .finally(() => {
            const temp = cloneDeep(interestData)
            temp.map((item, index) => {
              if (item.id === currentId) temp[index] = values as any
            })
            setInterestData(temp)
            setLoading(false)
          })
      }
    })
  }

  const onInterestRemove = async (id: any) => {
    return new Promise((resolve) => {
      setLoading(true)
      deleteTrustLedgerInterest(id)
        .then(() => {
          resolve(true)
        })
        .finally(() => {
          const temp: any = []
          interestData.map((item) => {
            if (item.id !== id) temp.push(item)
          })
          setInterestData(temp)
          setLoading(false)
        })
    })
  }

  /**
   * Move money off this bucket onto other loans' sub-ledgers. Re-reads the ledger afterwards
   * rather than splicing rows in: the transfer writes a row per destination and the balance
   * column is a running total, so the walk has to be redone from what was actually stored.
   */
  const onTransfer = async (data: Record<string, any>) => {
    const res = await transferTrustLedgerInterest(data)
    init()
    return res
  }

  const onInterestExportExcel = () => {
    const url = `${baseUrl}/loan/downloadTrustLedgerInterest/${loanNumber}?token=${token}&type=${tabType}`

    const windowReference: any = window.open()
    windowReference.document.title = 'Downloading...'
    windowReference.location = url
  }

  const interestInputLogic = (inputs: Record<string, InputType>, ID?: number, key?: string) => {
    if (key === 'payment') {
      inputs['deposit'].value = 0
    }
    if (key === 'deposit') {
      inputs['payment'].value = 0
    }
    return inputs
  }

  return (
    <div className="relative">
      <LayoutLoading show={loading} />

      <div className="flex justify-between items-center flex-wrap">
        <div className="font-variation-settings-600 text-lg">- {title}</div>
        <div className="flex items-center">
          <button className="hover:underline text-[14px] text-shade-blue p-2" onClick={() => setTransferring(true)}>
            <div className="flex items-center">
              <ArrowsRightLeftIcon className="w-4 h-4 mr-2" /> Transfer to Another Loan
            </div>
          </button>
          <button className="hover:underline text-[14px] text-shade-blue p-2" onClick={onInterestExportExcel}>
            <div className="flex items-center">
              <ArrowDownTrayIcon className="w-4 h-4 mr-2" /> Export to Excel
            </div>
          </button>
        </div>
      </div>
      {tabType === 'draw' && (
        <div className="grid grid-cols-2 gap-2">
          <span>Construction Reserve: ${getPrice2decimal(constructionReserve)}</span>
          <span>Remaining Construction Reserve: ${getPrice2decimal(currentBalance ?? constructionReserve)}</span>
        </div>
      )}
      {tabType === 'escrow' && (
        <div className="grid grid-cols-2 gap-2">
          <span>Original Escrow Amount: ${getPrice2decimal(originalEscrowAmount)}</span>
          <span>Current Escrow Amount: ${getPrice2decimal(currentBalance ?? originalEscrowAmount)}</span>
        </div>
      )}
      {tabType === 'escrowRepairHoldback' && (
        <div className="grid grid-cols-2 gap-2">
          <span>Original Escrow Repair Holdback: ${getPrice2decimal(escrowRepairHoldbackCommitment)}</span>
          <span>
            Current Escrow Repair Holdback: ${getPrice2decimal(currentBalance ?? escrowRepairHoldbackCommitment)}
          </span>
        </div>
      )}
      {tabType === 'interest' && (
        <div className="mb-2 space-y-1">
          <div>
            <span>Original Interest Reserve Balance: ${getPrice2decimal(removeComma(loan?.interestReserve ?? 0))}</span>
          </div>
          <div>
            <span>
              Interest Reserve Balance: $
              {getPrice2decimal(currentBalance ?? (interestReserve === -1 ? originalEscrowAmount : interestReserve))}
            </span>
          </div>
        </div>
      )}
      {transferring && (
        <SubLedgerTransferModal
          fromBucket={tabType}
          fromTitle={title}
          available={closingBalance}
          onSubmit={onTransfer}
          onClose={() => setTransferring(false)}
        />
      )}

      <FormTable
        header={interestHeader}
        addText={'Add New Item'}
        inputs={interestDefaultInputs}
        data={interestDataWithBalance}
        // Newest first on screen; the rows themselves stay in the order the balance was walked in.
        defaultSortKey={LEDGER_DEFAULT_SORT_KEY}
        defaultSortOrder={LEDGER_DEFAULT_SORT_ORDER}
        stickyActions
        inputLogic={interestInputLogic}
        cols={3}
        onAddPress={confirmPayoff}
        onSubmit={onInterestSubmit}
        onRemove={onInterestRemove}
      />
    </div>
  )
}
